import { inject as service } from '@ember/service';
import EmObject, { action, computed, get, set } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import Controller from '@ember/controller';
import { all, reject } from 'rsvp';
import { isEmpty, isNone, isPresent } from '@ember/utils';
import { task, waitForEvent } from 'ember-concurrency';
import Evented from '@ember/object/evented';
import GroupCondition from 'cio/app-models/conditions/group';
import {
  deserializeFilters as deserializeEventFilters,
  filterOperator as filterEventOperator,
} from 'cio/utils/conditions/deserialize/event';

import { serializeFilters } from 'cio/utils/conditions/serialize/event';
import { differentFilters } from 'cio/utils/filters';
import CampaignModel, {
  ATTRS,
  AudienceSettingsType,
  CampaignRestartModes,
  CampaignType,
  CampaignUpdateTypes,
  DATE_TRIGGERED_ATTRS,
  EventTriggeredCampaignAttributeFilters,
  FormTriggeredCampaignAttributeFilters,
  ObjectCampaignTrigger,
  RelationshipCampaignTrigger,
  ObjectRelationshipTriggerType,
  ObjectTriggerType,
  TYPE_NAMES_FOR_TRACKING,
} from 'cio/models/campaign';
import { CUSTOMER_TIME_ZONE, LOCAL_TIME_ZONE } from 'cio/utils/time-zones';
import { FrequencyOption } from 'cio/utils/campaigns/date-triggered';
import AuthService from 'cio/services/auth';
import EnvironmentService from 'cio/services/environment';
import AnalyticsService from 'cio/services/analytics';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import { SerializedLegacyAttributeFilters } from 'cio/types/conditions/legacy-filter';
import deserializeAndNormalizeFormAttributeFilters from 'cio/utils/campaigns/deserialize-normalize-form-attribute-filters';
import CustomFormsService from 'cio/services/custom-forms';
import { FormType } from 'cio/utils/forms';
import { tracked } from '@glimmer/tracking';
import AccountService from 'cio/services/account';
import { CampaignTypeTrackingName, CampaignTypeTriggerName } from 'cio/utils/campaigns/campaign-type-trigger-info';
import FeaturesService from 'ember-feature-flags/services/features';
import DS from 'ember-data';
import { ObjectAttributesKey } from 'cio/components/campaigns/trigger/setup/condition-blocks/object/config';
import ApiService from 'cio/services/api';
import AlertsService from 'cio/services/alerts';

export function deserializeEventAttributeFilters(
  filters: SerializedLegacyAttributeFilters,
  event: string,
): EventTriggeredCampaignAttributeFilters {
  if (isNone(filters)) {
    return GroupCondition.create({ operator: 'and' }) as EventTriggeredCampaignAttributeFilters;
  }

  return GroupCondition.create({
    operator: filterEventOperator(filters),
    children: deserializeEventFilters(filters, event),
  }) as EventTriggeredCampaignAttributeFilters;
}

export function deserializeFormAttributeFilters(
  filters: SerializedLegacyAttributeFilters,
): FormTriggeredCampaignAttributeFilters {
  return deserializeAndNormalizeFormAttributeFilters(filters);
}

const CONFIRM_CHANGES_MODAL_DECISION_MADE = 'confirmChangesModalDecisionMade';

class ConfirmChangesBus extends EmObject.extend(Evented) {
  cancel() {
    this.trigger(CONFIRM_CHANGES_MODAL_DECISION_MADE, false);
  }

  save() {
    this.trigger(CONFIRM_CHANGES_MODAL_DECISION_MADE, true);
  }
}

// keys that are used as "legacy" attributes
enum CampaignTopLevelKey {
  anchors = 'anchors',
  event = 'event',
  filters = 'filters',
  attributeFilters = 'attributeFilters',
  sendToUnsubscribed = 'sendToUnsubscribed',
  useMessageLimits = 'useMessageLimits',
  leadDays = 'leadDays',
  frequency = 'frequency',
  startTime = 'startTime',
  zone = 'zone',
  fallbackZone = 'fallbackZone',
  dateTriggeredFrequency = 'dateTriggeredFrequency',
  dateTriggeredLeadDays = 'dateTriggeredLeadDays',
  dateTriggeredStartTime = 'dateTriggeredStartTime',
  dateTriggeredAttribute = 'dateTriggeredAttribute',
  dateTriggeredZone = 'dateTriggeredZone',
  dateTriggeredFallbackZone = 'dateTriggeredFallbackZone',
}

// keys that are used as "tracked" attributes
enum CampaignTopLevelTrackedKeys {
  restartMinInterval = 'restartMinInterval',
  restartMode = 'restartMode',
  type = 'type',
  objectTypeId = 'objectTypeId',
  objectAttributeTriggers = 'objectAttributeTriggers',
  objectFilters = 'objectFilters',
  objectCampaign = 'objectCampaign',
  relationshipCampaign = 'relationshipCampaign',
  relationshipAttributeTriggers = 'relationshipAttributeTriggers',
  relationshipFilters = 'relationshipFilters',
  webhookId = 'webhookId',
}

export default class CampaignsCampaignSetupTriggerIndexController extends Controller {
  @service account!: AccountService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service analytics!: AnalyticsService;
  @service customForms!: CustomFormsService;
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service features!: FeaturesService;
  @service store!: DS.Store;
  @service api!: ApiService;
  @service alerts!: AlertsService;

  queryParams = ['triggerFormId', 'formType'];
  triggerFormId!: string;
  formType!: FormType | '';

  CampaignType = CampaignType;
  CampaignRestartModes = CampaignRestartModes;

  @readOnly('model') campaign!: CampaignModel;
  isCampaignOverview = false;

  anchors: Record<string, unknown>[] = [];
  event: string | null = null;
  filters: Record<string, unknown>[] = [];
  attributeFilters!: EventTriggeredCampaignAttributeFilters | FormTriggeredCampaignAttributeFilters;
  @tracked restartMinInterval: number | null = null;
  @tracked restartMode: CampaignRestartModes | null = null;
  @tracked type: CampaignType | null = null;
  sendToUnsubscribed = null;
  useMessageLimits = null;
  leadDays = 0;
  frequency = null;
  startTime = null;
  zone = null;
  fallbackZone = null;
  dateTriggeredFrequency: FrequencyOption | null = null;
  dateTriggeredLeadDays: number | null = null;
  dateTriggeredStartTime: string | null = null;
  dateTriggeredAttribute: string | null = null;
  dateTriggeredZone: string | null = null;
  dateTriggeredFallbackZone: string | null = null;
  @tracked audience: CampaignModel['audience'] | null = null;

  @tracked objectTypeId: number | null = null;
  @tracked objectAttributeTriggers: CampaignModel['objectAttributeTriggers'] = null;
  @tracked objectFilters: CampaignModel['objectFilters'] = null;
  @tracked relationshipAttributeTriggers: CampaignModel['relationshipAttributeTriggers'] = null;
  @tracked relationshipFilters: CampaignModel['relationshipFilters'] = null;
  @tracked objectCampaign: ObjectCampaignTrigger | null = null;
  @tracked relationshipCampaign: RelationshipCampaignTrigger | null = null;

  @tracked webhookId: string | null = null;

  _showChangeFrequencyModal = false;
  _showChangeDateTriggeredAttributesModal = false;
  _confirmChangesBus = ConfirmChangesBus.create();
  @tracked isDataCampaignsFeedbackModalVisible = false;
  @tracked selectedPreviewType: CampaignTypeTriggerName | null = null;
  @tracked showHelpModal = false;
  @tracked preMessageFilter = !this.features.isEnabled('campaign_trigger_ui_new');
  @tracked exitOnTriggerOrFilterNotMatched!: CampaignModel['exitOnTriggerOrFilterNotMatched'];
  @tracked exitOnConversionMatched!: CampaignModel['exitOnConversionMatched'];

  @computed(
    ...ATTRS,
    `campaign.{${ATTRS.join(',')}}`,
    'attributeFilters.isDirty',
    'isObjectTriggered',
    'isObjectRelationshipTriggered',
    'audience.{type,personFilters.[],relationshipFilters.[]}',
  )
  private get areRecipientSettingsDirty(): boolean {
    let { campaign, anchors, filters } = this;

    if (this.type === CampaignType.Webhook) {
      return this.type !== campaign.type;
    }

    if (this.isObjectRelationshipTriggered) {
      return (
        this.relationshipCampaign?.objectTypeId !== campaign.relationshipCampaign?.objectTypeId ||
        this.relationshipCampaign?.type !== campaign.relationshipCampaign?.type ||
        differentFilters(this.relationshipAttributeTriggers ?? [], campaign.relationshipAttributeTriggers ?? []) ||
        differentFilters(this.objectAttributeTriggers ?? [], campaign.objectAttributeTriggers ?? []) ||
        differentFilters(this.anchors ?? [], campaign.anchors ?? []) ||
        differentFilters(this.relationshipFilters ?? [], campaign.relationshipFilters ?? []) ||
        differentFilters(this.objectFilters ?? [], campaign.objectFilters ?? []) ||
        differentFilters(this.filters ?? [], campaign.filters ?? []) ||
        this.audience?.type !== campaign.audience?.type ||
        differentFilters(this.audience?.personFilters ?? [], campaign.audience?.personFilters ?? []) ||
        differentFilters(this.audience?.relationshipFilters ?? [], campaign.audience?.relationshipFilters ?? [])
      );
    }

    if (this.isObjectTriggered) {
      return (
        this.objectCampaign?.objectTypeId !== campaign.objectCampaign?.objectTypeId ||
        this.objectCampaign?.type !== campaign.objectCampaign?.type ||
        differentFilters(this.objectAttributeTriggers ?? [], campaign.objectAttributeTriggers ?? []) ||
        differentFilters(this.objectFilters ?? [], campaign.objectFilters ?? []) ||
        differentFilters(this.filters ?? [], campaign.filters ?? []) ||
        this.audience?.type !== campaign.audience?.type ||
        differentFilters(this.audience?.personFilters ?? [], campaign.audience?.personFilters ?? []) ||
        differentFilters(this.audience?.relationshipFilters ?? [], campaign.audience?.relationshipFilters ?? [])
      );
    }

    return (
      differentFilters(anchors, campaign.anchors) ||
      differentFilters(filters, campaign.filters) ||
      this.attributeFilters?.isDirty ||
      (
        [
          'event',
          'restartMinInterval',
          'restartMode',
          'sendToUnsubscribed',
          'useMessageLimits',
          ...DATE_TRIGGERED_ATTRS,
        ] as (keyof CampaignModel)[]
      ).some(
        (attr: keyof CampaignModel) =>
          get(this, attr as keyof CampaignsCampaignSetupTriggerIndexController) !== get(campaign, attr),
      )
    );
  }

  @computed('sendToUnsubscribed', 'campaign.sendToUnsubscribed')
  private get isSendToUnsubscribedDirty() {
    return this.sendToUnsubscribed !== this.campaign.sendToUnsubscribed;
  }

  @computed('useMessageLimits', 'campaign.useMessageLimits')
  private get isUseMessageLimitsDirty() {
    return this.useMessageLimits !== this.campaign.useMessageLimits;
  }

  @computed('areRecipientSettingsDirty', 'isSendToUnsubscribedDirty')
  get isDirty() {
    return this.areRecipientSettingsDirty || this.isSendToUnsubscribedDirty;
  }

  @computed('dateTriggeredZone', 'dateTriggeredFallbackZone')
  private get needsFallbackZone() {
    return this.dateTriggeredZone === CUSTOMER_TIME_ZONE.value && isEmpty(this.dateTriggeredFallbackZone);
  }

  @computed('type', 'campaign.isTypeNone', 'isInvalid')
  get showSaveTooltip() {
    return !this.isInvalid && this.type !== CampaignType.None && this.campaign.isTypeNone;
  }

  @computed('showSaveTooltip')
  get saveTooltipMessage() {
    if (!this.showSaveTooltip) {
      return '';
    }

    return "The Campaign's trigger type cannot be changed once you save.";
  }

  @computed(
    'type',
    'anchors',
    'event',
    'dateTriggeredAttribute',
    'dateTriggeredFrequency',
    'dateTriggeredLeadDays',
    'dateTriggeredMaxLeadDays',
    'dateTriggeredStartTime',
    'needsFallbackZone',
    'attributeFilters.{hasErrors,children.[]}',
    'webhookId',
    'objectTypeId',
    'objectAttributeTriggers',
    'objectFilters',
    'relationshipAttributeTriggers',
    'relationshipFilters',
    'isObjectTriggered',
    'isObjectRelationshipTriggered',
    'audience.{type,personFilters.[],relationshipFilters.[]}',
  )
  get isInvalid() {
    if (this.type === CampaignType.Behavioral) {
      return isEmpty(this.anchors) || this.hasOnlyNegateConditions;
    } else if (this.type === CampaignType.Transactional) {
      return isEmpty(this.event) || (this.attributeFilters?.hasErrors && !isEmpty(this.attributeFilters?.children));
    } else if (this.type === CampaignType.Form) {
      return this.attributeFilters?.hasErrors && !isEmpty(this.attributeFilters?.children);
    } else if (this.type === CampaignType.Date) {
      return (
        isEmpty(this.dateTriggeredAttribute) ||
        isEmpty(this.dateTriggeredFrequency) ||
        isEmpty(this.dateTriggeredStartTime) ||
        this.dateTriggeredMaxLeadDays < (this.dateTriggeredLeadDays ?? 0) ||
        this.needsFallbackZone
      );
    } else if (this.type === CampaignType.Webhook) {
      return this.webhookId == null;
    } else if (this.isObjectRelationshipTriggered) {
      let hasTriggers = true;
      if (this.relationshipCampaign?.type === ObjectRelationshipTriggerType.Updated) {
        // at least one Person or Object trigger need to bet set
        hasTriggers =
          !isEmpty(this.relationshipAttributeTriggers) ||
          !isEmpty(this.objectAttributeTriggers) ||
          !isEmpty(this.anchors);
      }
      let hasEmptyAudience =
        this.audience?.type === AudienceSettingsType.filtered
          ? [...(this.audience?.personFilters ?? []), ...(this.audience?.relationshipFilters ?? [])].length === 0
          : false;
      return (
        this.relationshipCampaign?.type == null ||
        this.relationshipCampaign?.objectTypeId == null ||
        !hasTriggers ||
        hasEmptyAudience
      );
    } else if (this.isObjectTriggered) {
      let hasTriggers = true;
      if (this.objectCampaign?.type === ObjectTriggerType.Updated) {
        hasTriggers = !isEmpty(this.objectAttributeTriggers);
      }
      let hasEmptyAudience =
        this.audience?.type === AudienceSettingsType.filtered
          ? [...(this.audience?.personFilters ?? []), ...(this.audience?.relationshipFilters ?? [])].length === 0
          : false;
      return (
        this.objectCampaign?.type == null ||
        this.objectCampaign?.objectTypeId == null ||
        !hasTriggers ||
        hasEmptyAudience
      );
    } else if (this.type === CampaignType.None) {
      return true;
    }

    return false;
  }

  get hasOnlyNegateConditions() {
    return this.anchors.every(({ negate }) => negate === true);
  }

  get isObjectTriggered() {
    return this.type === CampaignType.Object;
  }

  get isObjectRelationshipTriggered() {
    return this.type === CampaignType.ObjectRelationship;
  }

  get objectType() {
    let { objectTypeId } = this; // Date triggered campaign which uses Object attribute will have this value set.
    if (this.isObjectTriggered && this.objectCampaign?.objectTypeId != null) {
      // Object create/update/delete will have the ObjectTypeId nested under it's own configuration object
      objectTypeId = this.objectCampaign.objectTypeId;
    } else if (this.isObjectRelationshipTriggered && this.relationshipCampaign?.objectTypeId != null) {
      // Relationship create/update/delete will have the ObjectTypeId nested under it's own configuration object
      objectTypeId = this.relationshipCampaign.objectTypeId;
    }
    if (objectTypeId != null) {
      return this.store.peekRecord('object-type', objectTypeId);
    }
    return null;
  }

  @computed(
    'isInvalid',
    'type',
    'needsFallbackZone',
    'createWebhookEndpoint.isRunning',
    'isObjectTriggered',
    'isObjectRelationshipTriggered',
    'audience.{type,personFilters.[],relationshipFilters.[]}',
  )
  get invalidErrorMessage() {
    // TODO: create single getter for "inInvalid" and "invalidErrorMessage", return as object? Then extract boolean and message from that getter in the template

    if (!this.type) {
      return;
    }

    if (this.type === CampaignType.Webhook) {
      if (this.createWebhookEndpoint.isRunning) {
        return 'Please wait for the webhook URL to generate';
      }

      return;
    }

    if (this.type === CampaignType.Date && this.needsFallbackZone) {
      return 'Please select a fallback timezone to save your campaign!';
    }

    if (this.type === CampaignType.Behavioral && this.hasOnlyNegateConditions) {
      return 'Please ensure the segment trigger conditions do not contain "not in" conditions only.';
    }

    if (this.isObjectTriggered || this.isObjectRelationshipTriggered) {
      if (
        this.audience?.type === AudienceSettingsType.filtered &&
        [...(this.audience?.personFilters ?? []), ...(this.audience?.relationshipFilters ?? [])].length === 0
      ) {
        return 'Please specify the people in the audience, or choose a different audience type.';
      }
      return 'Please select Object Type and trigger conditions!';
    }

    let label = this.type === CampaignType.Transactional ? 'event' : 'condition';
    return `Please set up your trigger ${label} to save your campaign!`;
  }

  @computed('dateTriggeredFrequency')
  get dateTriggeredMaxLeadDays() {
    switch (this.dateTriggeredFrequency) {
      case FrequencyOption.once:
        return Infinity;
      case FrequencyOption.monthly:
        return 28;
      case FrequencyOption.yearly:
        return 364;
      default:
        return Infinity;
    }
  }

  @computed('filters', 'restartMode', 'type')
  get areFrequencyControlsDisabled() {
    return (
      (isPresent(this.filters) && this.restartMode === 'none' && this.type === CampaignType.Behavioral) ||
      this.type === CampaignType.Object ||
      this.type === CampaignType.ObjectRelationship
    );
  }

  @computed('type', 'isObjectTriggered', 'isObjectRelationshipTriggered')
  get shouldShowFilterControls() {
    if (this.type == null) {
      return false;
    }

    return (
      [CampaignType.Behavioral, CampaignType.Transactional, CampaignType.Date, CampaignType.Form].includes(this.type) ||
      this.isObjectTriggered ||
      this.isObjectRelationshipTriggered
    );
  }

  @computed('type')
  get shouldShowFrequencyControls() {
    if (this.type == null) {
      return false;
    }

    return [
      CampaignType.Behavioral,
      CampaignType.Transactional,
      CampaignType.Form,
      CampaignType.Object,
      CampaignType.ObjectRelationship,
    ].includes(this.type);
  }

  async prepareForEdit(campaign: CampaignModel) {
    // if triggerFormId query param is present, use it to pre-set campaign's form trigger:
    if (this.triggerFormId && campaign.type === CampaignType.None) {
      campaign.setProperties({
        event: 'form_submit',
        type: CampaignType.Form,
        attributeFilters: {
          or: [
            {
              field: 'form_id',
              form_type: this.formType,
              inverse: false,
              operator: 'eq',
              value: this.triggerFormId.toLowerCase(), // lower-cased by the back-end on save, but let's do it here to be more explicit
            },
          ],
        },
        updateType: CampaignUpdateTypes.Recipients,
      });

      try {
        await campaign.save();
        this.set('triggerFormId', null);
        this.set('formType', null);
      } catch (e) {
        campaign.rollbackAttributes();
      }
    }

    let attrs = campaign.getProperties(...ATTRS);
    let attributeFilters = this.campaign.isFormTriggered
      ? deserializeFormAttributeFilters(attrs.attributeFilters)
      : deserializeEventAttributeFilters(attrs.attributeFilters, attrs.event);

    let { audience } = attrs;
    if (audience == null) {
      audience = {
        type: AudienceSettingsType.self,
        personFilters: [],
        relationshipFilters: [],
      };
    }
    this.setProperties({
      ...(attrs as Record<string, unknown>), // type hack
      attributeFilters,
      audience,
      selectedPreviewType: null,
    });
  }

  saveCampaignTask = task(async () => {
    let {
      campaign,
      anchors,
      event,
      filters,
      attributeFilters,
      restartMinInterval,
      restartMode,
      type,
      dateTriggeredFrequency,
      dateTriggeredLeadDays,
      dateTriggeredAttribute,
      dateTriggeredStartTime,
      dateTriggeredZone,
      dateTriggeredFallbackZone,
      objectTypeId,
      objectFilters,
      objectAttributeTriggers,
      relationshipAttributeTriggers,
      relationshipFilters,
      audience,
      objectCampaign,
      relationshipCampaign,
      webhookId,
    } = this;

    if (attributeFilters.children.length > 0 && !attributeFilters.validate()) {
      await reject();
    }

    // Show confirmation modal if we're switching between frequency types, or
    // switching it off on a live campaign.
    if (
      !campaign.isDraft &&
      ((campaign.restartMode === CampaignRestartModes.Rematch &&
        (restartMode === CampaignRestartModes.None || restartMode === CampaignRestartModes.Interval)) ||
        (campaign.restartMode === CampaignRestartModes.Interval &&
          (restartMode === CampaignRestartModes.None || restartMode === CampaignRestartModes.Rematch)))
    ) {
      this.set('_showChangeFrequencyModal', true);

      let shouldSave = (await waitForEvent(
        this._confirmChangesBus,
        CONFIRM_CHANGES_MODAL_DECISION_MADE,
      )) as unknown as boolean;
      this.set('_showChangeFrequencyModal', false);

      if (!shouldSave) {
        await reject();
      }
    }

    if (
      !campaign.isDraft &&
      DATE_TRIGGERED_ATTRS.some(
        (attr: keyof CampaignModel) =>
          get(this, attr as keyof CampaignsCampaignSetupTriggerIndexController) !== get(campaign, attr),
      )
    ) {
      this.set('_showChangeDateTriggeredAttributesModal', true);

      let shouldSave = (await waitForEvent(
        this._confirmChangesBus,
        CONFIRM_CHANGES_MODAL_DECISION_MADE,
      )) as unknown as boolean;
      this.set('_showChangeDateTriggeredAttributesModal', false);

      if (!shouldSave) {
        await reject();
      }
    }

    try {
      // Potentially calls save on the campaign twice, because the trigger page
      // shows both trigger settings, and send to unsubscribe settings. These
      // two have different updateTypes.
      if (this.areRecipientSettingsDirty || (type === CampaignType.Webhook && this.webhookId != null)) {
        if (type !== CampaignType.Webhook) {
          webhookId = null;
        }

        if (type !== CampaignType.Date) {
          // Date triggered campaign which uses Object attribute will have this value set. So for any other type it is not used.
          objectTypeId = null;
        }

        if (type !== CampaignType.Object) {
          // Object create/update/delete has it's own config set to "objectCampaign", other types are not using it
          objectCampaign = null;
        }
        if (type !== CampaignType.ObjectRelationship) {
          // Relationship create/update/delete has it's own config set to "objectCampaign", other types are not using it
          relationshipCampaign = null;
          // "relationshipAttributeXY" are only used in Relationship trigger types
          relationshipAttributeTriggers = null;
          relationshipFilters = null;
        }
        if (type !== CampaignType.Object && type !== CampaignType.ObjectRelationship) {
          // Object & Relationship types use these two "objectAttributeXY" filters&triggers, other types are not using them
          objectFilters = null;
          objectAttributeTriggers = null;
          // Only Object & Relationship types currently support "Audience" settings
          audience = null;
        }

        campaign.setProperties({
          event,
          anchors,
          filters,
          restartMinInterval,
          restartMode,
          type,
          dateTriggeredFrequency,
          dateTriggeredLeadDays,
          dateTriggeredAttribute,
          dateTriggeredStartTime,
          dateTriggeredZone,
          dateTriggeredFallbackZone,
          attributeFilters: serializeFilters(attributeFilters.children, attributeFilters.operator, true, true),
          webhookId,
          objectTypeId,
          objectFilters,
          objectAttributeTriggers,
          relationshipAttributeTriggers,
          relationshipFilters,
          audience,
          objectCampaign,
          relationshipCampaign,
          updateType: CampaignUpdateTypes.Recipients,
        });
        await campaign.save();
      }

      if (!campaign.isWebhookTriggered && (this.isSendToUnsubscribedDirty || this.isUseMessageLimitsDirty)) {
        campaign.setProperties({
          sendToUnsubscribed: this.sendToUnsubscribed,
          useMessageLimits: this.useMessageLimits,
          updateType: 'global_settings',
        });

        await campaign.save();
      }

      await this.prepareForEdit(campaign);
      this.alerts.success('campaign.settings.saved', 'Changes saved');
    } catch (errors) {
      campaign.rollbackAttributes();
    }
  });

  resetTask = task(async () => {
    this.campaign.rollbackAttributes();
    await this.prepareForEdit(this.campaign);
    this.analytics.track('campaign', 'edit_discarded', { model: this.campaign, from: 'trigger' });
  });

  @action
  updateFilters(conditions: Record<string, unknown>[]) {
    this.set('filters', conditions);
  }

  @action
  updateTriggers(conditions: Record<string, unknown>[]) {
    this.set('anchors', conditions);
  }

  createWebhookEndpoint = task(async () => {
    let webhookId = await this.campaign.createWebhookEndpoint({});
    this.webhookId = webhookId;
  });

  changeTypeTask = task(
    async (
      type: CampaignType,
      objectTypeId?: string,
      subType?: ObjectTriggerType | ObjectRelationshipTriggerType,
      trackingValue?: string,
    ) => {
      if (type === CampaignType.Form) {
        // Ensure FB forms are loaded for the form trigger type:
        await all([this.facebookLeadAds.initialize(), this.customForms.initialize()]);

        // form-triggered campaigns react to "form_submit" events, their top-level attributeFilters have "or" operator (to allow multiple form triggers):
        this.set('event', 'form_submit');
        set(this.attributeFilters, 'operator', 'or');
      } else if (this.type === CampaignType.Form) {
        // revert values specific to form-triggered campaigns, which shouldn't be set for other types:
        this.set('event', '');
        set(this.attributeFilters, 'children', []);
        set(this.attributeFilters, 'operator', 'and');
      }

      // if the webhook id is not null, it's probably because the user switched
      // between campaign types, and a webhook endpoint was already created. In
      // this case, let's keep using the same webhook endpoint.
      if (type === CampaignType.Webhook && this.webhookId == null) {
        this.createWebhookEndpoint.perform();
      }

      // Objects & Relationships logic below
      if (type === CampaignType.Object && objectTypeId != null && subType != null) {
        this.objectCampaign = {
          objectTypeId: parseInt(objectTypeId),
          type: subType as ObjectTriggerType,
        };
        // These 2 "objectAttribute(Filters|Triggers)" are used for all Object / Relationship types, thus we need to reset these values on type change
        this.objectFilters = [];
        this.objectAttributeTriggers = [];
        this.audience = {
          type: AudienceSettingsType.related,
          personFilters: [],
          relationshipFilters: [],
        };
        // Clear data that's not related to Object trigger type
        this.relationshipCampaign = null;
      } else if (type === CampaignType.ObjectRelationship && objectTypeId != null && subType != null) {
        this.relationshipCampaign = {
          objectTypeId: parseInt(objectTypeId),
          type: subType as ObjectRelationshipTriggerType,
        };
        // These 4 "objectAttribute(Filters|Triggers)" and "relationshipAttribute(Filters|Triggers)" are used for all Relationship types, thus we need to reset these values on type change
        this.objectFilters = [];
        this.objectAttributeTriggers = [];
        this.relationshipAttributeTriggers = [];
        this.relationshipFilters = [];
        this.audience = {
          type: AudienceSettingsType.self,
          personFilters: [],
          relationshipFilters: [],
        };
        // Clear data that's not related to Object trigger type
        this.objectCampaign = null;
      } else {
        // If type is not any of available Object / Relationship types, then we want to reset these values
        this.objectFilters = null;
        this.objectAttributeTriggers = null;
        this.relationshipAttributeTriggers = null;
        this.relationshipFilters = null;
        this.objectCampaign = null;
        this.relationshipCampaign = null;
        this.audience = null;

        if (type !== CampaignType.Date) {
          // delete `objectTypeId` if trigger type is not "date"
          this.objectTypeId = null;
        }
      }

      this.type = type;

      // Set defaults for date triggered campaigns
      if (type === CampaignType.Date) {
        this.set('dateTriggeredFrequency', 'once');

        if (this.dateTriggeredZone == null || this.dateTriggeredZone === '') {
          this.set('dateTriggeredZone', LOCAL_TIME_ZONE.value);
        }

        if (this.dateTriggeredStartTime == null || this.dateTriggeredStartTime === '') {
          this.set('dateTriggeredStartTime', '12:00');
        }
      }

      this.trackTriggerTypeViewed(trackingValue);
    },
  );

  @action
  updateTopLevelValue<K extends CampaignTopLevelKey | CampaignTopLevelTrackedKeys, V extends (typeof this)[K]>(
    key: K,
    _value: V | null,
  ) {
    let value = _value; // might get overwritten on special cases

    if (key === CampaignTopLevelTrackedKeys.objectTypeId && (isEmpty(value) || Number(value) === 0)) {
      value = null;
    }

    this.set(key, value);
  }

  @action
  updateTopLevelValues<K extends CampaignTopLevelKey | CampaignTopLevelTrackedKeys, V extends (typeof this)[K]>(
    values: Record<K, V | null>,
  ) {
    this.setProperties(values);
  }

  @action
  updateObjectAttributeConditions(key: ObjectAttributesKey, filters: CampaignModel[ObjectAttributesKey]) {
    this[key] = filters;
  }

  @action
  cancelChanges() {
    this._confirmChangesBus.cancel();
  }

  @action
  confirmChanges() {
    this._confirmChangesBus.save();
  }

  @action
  reset() {
    return this.resetTask.perform();
  }

  // Used by Old UI
  // TODO: remove this when doing Old UI cleanup (when removing "campaign_trigger_ui_new" feature flag)
  @action
  changeSelectedPreviewType(type: CampaignTypeTriggerName) {
    this.selectedPreviewType = type;
    this.trackTriggerTypeViewed();
  }

  @action
  toggleDataCampaignsFeedbackModal(show: boolean) {
    this.isDataCampaignsFeedbackModalVisible = show;
  }

  @action
  toggleHelpModal(showHelpModal: boolean) {
    this.showHelpModal = showHelpModal;
  }

  private trackTriggerTypeViewed(trackingValue?: string) {
    if (this.features.isEnabled('campaign_trigger_ui_new')) {
      if (this.type !== null && this.type !== CampaignType.None) {
        this.analytics.track('campaign', 'trigger_tab_viewed', {
          model: this.campaign,
          selectedTrigger: trackingValue ?? TYPE_NAMES_FOR_TRACKING[this.type],
          triggerTabType: 'Configure',
        });
      }
    } else {
      // Trigger tab type is whether a user is 'Select'ing between triggers and seeing preview descriptions
      // or 'Configure'ing after having already selected which trigger they want
      let triggerTabType = this.type === CampaignType.None ? 'Select' : 'Configure';

      this.analytics.track('campaign', 'trigger_tab_viewed', {
        model: this.campaign,
        selectedTrigger: CampaignTypeTrackingName[this.selectedPreviewType as CampaignTypeTriggerName],
        triggerTabType,
      });
    }
  }

  @action
  onUpdateRestartMinInterval(value: number) {
    this.restartMinInterval = value;
  }

  @action
  onUpdateRestartMode(value: CampaignRestartModes) {
    this.restartMode = value;
  }
}
